'use client';

/**
 * API namespace `market` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L3761-L4730).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';


import { disputes as adminMarketDisputes } from './admin-market-disputes';
import { reviews as adminMarketReviews } from './admin-market-reviews';
import { bundles as adminMarketBundles } from './admin-market-bundles';
import { coupons as adminMarketCoupons } from './admin-market-coupons';
import { giftCards as adminMarketGiftCards } from './admin-market-gift-cards';
import { products as adminMarketProducts } from './admin-market-products';
export const market = {
  // ─── Admin disputes (4 methods) ──────────────────────────────────────
  //
  // Mounted under `/api/admin/market/disputas/*` (Task B —
  // `admin-dispute.routes.ts`). All 4 endpoints require
  // `requireSuperadminOrAdmin` (admin CAN resolve + view; only
  // `appeal/resolve` is superadmin-only — Q5).
  // ─── Admin reviews moderation (1 method) ──────────────────────────────
  //
  // Mounted under `/api/admin/market/reviews/*` (Task B —
  // `admin-dispute.routes.ts` moderate handler). The route is
  // SEPARATE from the disputes router (per SPEC §5.2 — the path is
  // `/api/admin/market/reviews/:reviewId/moderate`, NOT `/api/admin/
  // market/disputas/reviews/:reviewId/moderate`).
  // ─── Admin bundles moderation (3 methods) ───────────────────────────
  //
  // SPEC-242 Track D #21 (Admin UI for ProductBundle approve/reject).
  //
  // Mounted under `/api/admin/market/bundles/*` (Task D #18 —
  // `product-bundle.controller.ts` handlers `listAllBundles` +
  // `approveBundle` + `rejectBundle`, registered on `admin.route.ts`).
  // All 3 endpoints inherit the router-level `requireAuth +
  // requireAdminRead` (admin role + JWT). The 2 mutation endpoints
  // (`approve` + `reject`) ALSO have per-route `requireAdmin` +
  // `requireRecent2FA` middleware (INV-SEC-06 §9.6 — admin moderation
  // of a seller's bundle is a financial-grade state transition:
  // approval makes the bundle visible in the public catalog → can
  // generate sales → affects seller revenue). The list endpoint is
  // read-only — NO 2FA required.
  //
  // §6 Anti-Invención: NO invented shapes — request + response types
  // come from `@profileia/shared/types/product-bundle.ts` (Track D
  // #17 — `ProductBundleListResponse` + `ProductBundleStatus` +
  // `ProductBundle`). The Zod `bundleRejectSchema` (1-500 chars
  // reason) is the canonical body validator — the frontend mirrors
  // the 10-500 length hint at the textarea layer (UX affordance; the
  // backend is the source of truth).
  //
  // 2FA: when the backend returns 403 `2FA_REQUIRED` on `approve` or
  // `reject` (the 5-min recent-2FA window expired between verify +
  // mutation), the frontend re-opens the `TwoFactorFinancialDialog`
  // (same pattern as `api.admin.market.disputes.resolve` + the wallet
  // withdraw + seller bundle delete flows). The 2FA dialog itself is
  // rendered by the admin page (`/admin/market/combos/page.tsx`) —
  // the api-client stays a thin typed wrapper.
  //
  // Path param: `:id` is `ProductBundle.bundleId` (UUID) — NOT the
  // Mongo ObjectId (per `backend/src/routes/admin.route.ts` line 790
  // doc comment + `product-bundle.service.approveBundle` /
  // `rejectBundle` lookups).
  //
  // Spec ref: SPEC-242 §4.4 (admin bundle endpoints — listAllBundles
  //           + approveBundle + rejectBundle), §4.3 (reglas de negocio
  //           — INV-MKT-20 admin approval gate), §4.5.2 (admin UI
  //           for bundle moderation — Track D #21);
  //           SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6 (INV-SEC-06 — 2FA
  //           for admin moderation) + §10.14 (combos).
  // ─── Admin coupons moderation (3 methods) ───────────────────────────
  //
  // SPEC-242 Track C #16 (Admin UI for Cuponera — approve/reject
  // coupons). Mounted under `/api/admin/market/coupons/*` (Track C #12
  // backend — `coupon.controller.ts` handlers `listAllCoupons` +
  // `approveCoupon` + `rejectCoupon`, registered on `admin.route.ts`,
  // merged in PR #628). All 3 endpoints inherit the router-level
  // `requireAuth + requireAdminRead` (admin role + JWT). The 2 mutation
  // endpoints (`approve` + `reject`) ALSO have per-route `requireAdmin`
  // + `requireRecent2FA` middleware (INV-SEC-06 §9.6 — admin moderation
  // of a seller's coupon is a financial-grade state transition:
  // approval makes the coupon visible in the public catalog → can
  // generate sales → affects seller revenue). The list endpoint is
  // read-only — NO 2FA required.
  //
  // §6 Anti-Invención: NO invented shapes — request + response types
  // come from `@profileia/shared/types/coupon.ts` (Track C #9 —
  // `CouponListResponse` + `CouponStatus` + `Coupon` + `CouponResponse`).
  // The Zod `rejectReasonSchema` (10-500 chars reason) is the canonical
  // body validator — the frontend mirrors the 10-500 length hint at the
  // textarea layer (UX affordance; the backend is the source of truth).
  //
  // 2FA: when the backend returns 403 `2FA_REQUIRED` on `approve` or
  // `reject` (the 5-min recent-2FA window expired between verify +
  // mutation), the frontend re-opens the `TwoFactorFinancialDialog`
  // (same pattern as `api.admin.market.bundles.*` + the wallet withdraw
  // + seller coupon delete flows). The 2FA dialog itself is rendered by
  // the admin page (`/admin/market/cupones/page.tsx`) — the api-client
  // stays a thin typed wrapper.
  //
  // Path param: `:couponId` is `Coupon.couponId` (UUID) — NOT the
  // Mongo ObjectId (per `backend/src/routes/admin.route.ts` line 880
  // doc comment + `coupon.service.approveCoupon` / `rejectCoupon`
  // lookups).
  //
  // Spec ref: SPEC-242 §3 (Track C — Cuponera Cuponatic-style APROBADO
  //           por Founder 2026-09-19), §3.5 (admin coupon endpoints —
  //           listAllCoupons + approveCoupon + rejectCoupon), §3.4.1
  //           (reglas de negocio — INV-MKT-20 admin approval gate),
  //           §3.6.5 (admin UI for coupon moderation — Track C #16);
  //           SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6 (INV-SEC-06 — 2FA
  //           for admin moderation) + §10.13 (Cuponera Externa).
  // ─── Admin gift cards moderation (3 methods) ────────────────────────
  //
  // SPEC-242 Track C #16 (Admin UI for Cuponera — approve/reject gift
  // cards). Mounted under `/api/admin/market/gift-cards/*` (Track C #12
  // backend — `coupon.controller.ts` handlers `listAllGiftCards` +
  // `approveGiftCard` + `rejectGiftCard`, registered on
  // `admin.route.ts`, merged in PR #628). All 3 endpoints inherit the
  // router-level `requireAuth + requireAdminRead` (admin role + JWT).
  // The 2 mutation endpoints (`approve` + `reject`) ALSO have
  // per-route `requireAdmin` + `requireRecent2FA` middleware
  // (INV-SEC-06 §9.6 — same financial-grade rationale as coupon
  // moderation). The list endpoint is read-only — NO 2FA required.
  //
  // §6 Anti-Invención: NO invented shapes — request + response types
  // come from `@profileia/shared/types/coupon.ts` (Track C #9 —
  // `GiftCardListResponse` + `GiftCardStatus` + `GiftCard` +
  // `GiftCardResponse`).
  //
  // 2FA: when the backend returns 403 `2FA_REQUIRED` on `approve` or
  // `reject`, the frontend re-opens the `TwoFactorFinancialDialog`
  // (rendered by `/admin/market/gift-cards/page.tsx`). Same pattern as
  // `api.admin.market.coupons.*`.
  //
  // Path param: `:giftCardId` is `GiftCard.giftCardId` (UUID).
  //
  // Spec ref: SPEC-242 §3 (Track C — Cuponera Cuponatic-style APROBADO
  //           por Founder 2026-09-19), §3.5 (admin gift card endpoints
  //           — listAllGiftCards + approveGiftCard + rejectGiftCard),
  //           §3.4.2 (reglas de negocio — INV-MKT-20 admin approval
  //           gate), §3.6.5 (admin UI for gift card moderation —
  //           Track C #16);
  //           SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6 (INV-SEC-06 — 2FA
  //           for admin moderation) + §10.13 (Cuponera Externa).
  // ─── Admin products moderation (4 methods) ───────────────────────────
  //
  // Sprint 138 (Founder directive 2026-09-22) — "fui al admin y no
  // encontré donde aprobar la publicación ni nada". The backend
  // endpoints existed since SPEC-081 Fase 3 (PR-3 Track C #11 —
  // `admin.market.controller.ts` lines 493-728 expose
  // `listPendingProducts` + `listAllProducts` + `approveProduct` +
  // `rejectProduct`). The frontend api-client was missing the typed
  // wrappers + the admin UI page was missing entirely.
  //
  // Mounted under `/api/admin/market/products/*` (Track C #11 backend
  // — `admin.market.controller.ts`, registered on `admin.route.ts`
  // lines 746-758). All 4 endpoints inherit the router-level
  // `requireAuth + requireAdminRead` (admin role + JWT). The 2
  // mutation endpoints (`approve` + `reject`) ALSO have per-route
  // `requireAdmin + requireRecent2FA` middleware (INV-SEC-06 §9.6 —
  // admin moderation of a seller's product is a financial-grade state
  // transition: approval makes the product visible in the public
  // catalog → can generate sales → affects seller revenue). The list
  // endpoints are read-only — NO 2FA required.
  //
  // §6 Anti-Invención: NO invented shapes — the response type
  // `AdminProductListItem` mirrors the backend
  // `PendingProductWithSeller` interface at
  // `backend/src/services/market.service.ts:213-266` (single source
  // of truth). The 2FA + 2 mutation flows follow the existing pattern
  // from `api.admin.market.bundles.*` + `api.admin.market.coupons.*`.
  //
  // 2FA: when the backend returns 403 `2FA_REQUIRED` on `approve` or
  // `reject` (the 5-min recent-2FA window expired between verify +
  // mutation), the frontend re-opens the `TwoFactorFinancialDialog`
  // (same pattern as `api.admin.market.bundles.*`). The 2FA dialog
  // itself is rendered by the admin page (`/admin/market/productos/
  // page.tsx`) — the api-client stays a thin typed wrapper.
  //
  // Path param: `:productId` is `MarketProduct.productId` (UUID).
  //
  // Spec ref: SPEC-081 Fase 3 §5.3 (admin market product routes), §4.1
  //           (listPendingProducts + approveProduct + rejectProduct
  //           service methods), §3.1 (ProductStatus enum), INV-MKT-20
  //           (admin approval gate); SPEC-NEGOCIO §10.4 (INV-MKT-20)
  //           + §9.6 (INV-SEC-06 2FA for financial-grade mutations).

  disputes: adminMarketDisputes,
  reviews: adminMarketReviews,
  bundles: adminMarketBundles,
  coupons: adminMarketCoupons,
  giftCards: adminMarketGiftCards,
  products: adminMarketProducts,
};
